/**
 * Map merged_orders rows (batch CSV or truth v2) into Profit Report order records.
 */

import type { TruthMergedOrderRow } from '../types/profitabilityTruth';
import { deduplicateOrders, type OrderDedupAccessors } from './orderDedup';

/** Order row shape used by ProfitabilityReport Order section. */
export interface MergedOrderProfitRecord {
  orderId: string;
  vehicleId: string;
  date: string;
  revenue: number;
  cost: number;
  profit: number;
  currency: string;
  status: string;
  revenue_imputed?: number;
  order_import_integration_type?: string;
  origin_city?: string;
  destination_city?: string;
  origin_postal_code?: string;
  destination_postal_code?: string;
  origin_end_timestamp_local?: string;
  destination_end_timestamp_local?: string;
  vehicle_assigned?: string;
  vehicle_pickup?: string;
  /** Hub depot number from getnotes (truth layer) for map Handoff pin. */
  handoff_depot?: string;
  vehicle_observed?: string;
  service_level_name?: string;
  origin_latitude?: number | null;
  origin_longitude?: number | null;
  destination_latitude?: number | null;
  destination_longitude?: number | null;
  /** 'postcode_centroid' = approximate position filled from the postcode. */
  destination_coords_source?: 'postcode_centroid';
  goods_quantity?: string | number;
  goods_weight?: string | number;
  customer?: string;
  allocation_method?: 'marginal' | 'fallback_equal_split';
}

function hasOrderAllocation(raw: MergedOrderInput): boolean {
  const r = raw as TruthMergedOrderRow;
  return r.allocation_method != null || r.allocated_cost_gbp != null;
}

type MergedOrderInput = TruthMergedOrderRow | Record<string, unknown>;

function numOrZero(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function optionalCoord(value: unknown): number | undefined {
  if (value == null || value === '') return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

const mergedOrderProfitRecordAccessors: OrderDedupAccessors<MergedOrderProfitRecord> = {
  orderId: (r) => r.orderId,
  customer: (r) => r.customer,
  date: (r) => r.origin_end_timestamp_local || r.destination_end_timestamp_local,
  origin: (r) => r.origin_postal_code || r.origin_city,
  destination: (r) => r.destination_postal_code || r.destination_city,
  revenue: (r) => r.revenue,
  vehicleAssigned: (r) => r.vehicle_assigned,
  vehicleObserved: (r) => r.vehicle_observed,
  // vehicle_pickup is informational for multi-leg; richness still uses assigned + observed
};

/**
 * Deduplicate by orderId (pass 1), then by composite key for opaque-id rows (pass 2).
 * Pass 2 catches Same Day Tracker fragments with different fingerprints but same route/day.
 */
function deduplicateByOrderId(records: MergedOrderProfitRecord[]): MergedOrderProfitRecord[] {
  return deduplicateOrders(records, mergedOrderProfitRecordAccessors, (r) => {
    // No fallback entity id is available on this row shape (unlike the backend's
    // buildMergedOrderRecord, which falls back to orderEntityId) — log so a dropped
    // row is at least traceable back to source data rather than silently vanishing.
    console.warn('[mergedOrderProfitRecords] dropping row with empty order_id', {
      date: r.date, customer: r.customer, revenue: r.revenue,
    });
  });
}

export function mapMergedOrderRowsToProfitRecords(
  rows: MergedOrderInput[],
): MergedOrderProfitRecord[] {
  const mapped = rows.map((raw) => {
    const r = raw as TruthMergedOrderRow;
    const revenue = numOrZero(r.total_revenue_tenant_currency);
    const allocated = hasOrderAllocation(raw);
    const cost = allocated ? numOrZero(r.allocated_cost_gbp) : 0;
    const profit = allocated
      ? (r.profit_gbp != null ? numOrZero(r.profit_gbp) : revenue - cost)
      : revenue;
    const destinationDate = (r.destination_end_timestamp_local || '').trim();
    const originDate = (r.origin_end_timestamp_local || '').trim();
    return {
      orderId: (r.order_id || '').trim(),
      vehicleId: (r.vehicle_observed || r.vehicle_assigned || '').trim(),
      date: destinationDate || originDate,
      revenue,
      cost,
      profit,
      currency: (r.tenant_currency || 'GBP').trim(),
      status: (r.status || '').trim(),
      revenue_imputed: r.revenue_imputed != null ? Number(r.revenue_imputed) : 0,
      allocation_method: r.allocation_method,
      order_import_integration_type: (r.order_import_integration_type || '').trim() || undefined,
      origin_city: (r.origin_city || '').trim(),
      destination_city: (r.destination_city || '').trim(),
      origin_postal_code: (r.origin_postal_code || '').trim(),
      destination_postal_code: (r.destination_postal_code || '').trim(),
      origin_end_timestamp_local: (r.origin_end_timestamp_local || '').trim(),
      destination_end_timestamp_local: (r.destination_end_timestamp_local || '').trim(),
      vehicle_assigned: (r.vehicle_assigned || '').trim(),
      vehicle_pickup: (r.vehicle_pickup || '').trim(),
      handoff_depot: (r.handoff_depot || '').trim() || undefined,
      vehicle_observed: (r.vehicle_observed || '').trim(),
      service_level_name: (r.service_level_name || '').trim(),
      origin_latitude: optionalCoord(r.origin_latitude),
      origin_longitude: optionalCoord(r.origin_longitude),
      destination_latitude: optionalCoord(r.destination_latitude),
      destination_longitude: optionalCoord(r.destination_longitude),
      ...(r.destination_coords_source === 'postcode_centroid' && {
        destination_coords_source: 'postcode_centroid' as const,
      }),
      goods_quantity:
        r.goods_quantity != null && r.goods_quantity !== ''
          ? (typeof r.goods_quantity === 'number' ? r.goods_quantity : String(r.goods_quantity).trim())
          : undefined,
      goods_weight:
        r.goods_weight != null && r.goods_weight !== ''
          ? (typeof r.goods_weight === 'number' ? r.goods_weight : String(r.goods_weight).trim())
          : undefined,
      customer: (r.customer ?? '').trim() || undefined,
    };
  });
  return deduplicateByOrderId(mapped);
}
